/**
 * The section that says where the connection goes (Option C, C2): Server (SQL Server, PostgreSQL, MySQL, Oracle), File
 * (SQLite), Cluster (Azure Data Explorer) or Storage (Azure Storage). Mode first — the choice that decides the other
 * rows leads: Oracle's "Connect by", Azure Storage's "Connect using". Then the target, the sign-in rows and the
 * database, in that order for every engine; SQLite's and Azure Storage's Access row ("Open read-only") ends the section.
 */
import type { AzureDiscoveredResource } from '@/types/azureDiscovery';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { ConnectionFormValues, DatabaseEngine } from '../../components/connectionFormSchema';
import { OracleConnectByRow, OracleTargetRows } from '../../components/OracleConnectionFields';
import { AzureStorageConnectionFields } from '../../components/AzureStorageConnectionFields';
import { FormRow } from '../pane/FormRow';
import { FormSection } from '../pane/FormSection';
import { DatabaseRows } from './DatabaseRows';
import { ServerAndPortRows, ServerRow } from './ServerRows';
import { SignInRows } from './SignInRows';
import { CHECK_ROW_CLASS, CHECKBOX_CLASS, type ConnectionFormApi } from './fieldControls';
import { targetSectionTitle } from './fieldLabels';

export type TargetSectionProps = {
  form: ConnectionFormApi;
  engine: DatabaseEngine;
  authentication: ConnectionFormValues['authentication'];
  loadedDatabases: string[];
  onLoadDatabases?: () => void;
  loadingDatabases: boolean;
  defaultHint?: { field: string; value: string } | null;
  onDismissDefaultHint?: () => void;
  /** The engine label whose draft the host was copied from (UX-20). */
  serverCarriedFrom?: string | null;
  onAzurePick: (resource: AzureDiscoveredResource) => void;
  entryDisabledReason?: string;
};

/**
 * Access: "Open read-only" — SQLite opens the file read-only; an Azure Storage connection turns off every change (uploads,
 * deletes, tier, metadata, queue and entity writes) for the user and for AI agents, while browsing, downloads and peeks
 * stay. One row for both: same label, the same `readOnly` field, the last row of the target section.
 */
function ReadOnlyRow({ form, engine }: { form: ConnectionFormApi; engine: DatabaseEngine }) {
  const { track } = useAnalytics();
  const hint = engine === 'azurestorage'
    ? 'Browsing, downloads and peeks stay available; uploads, deletes and other changes are turned off, also for AI agents.'
    : undefined;
  return (
    <form.Field name="readOnly">
      {(field: any) => (
        <FormRow row="readOnly" label="Access" groupId="connection-access-label" hint={hint}>
          <div className={CHECK_ROW_CLASS}>
            <input
              id="readOnly"
              name="readOnly"
              type="checkbox"
              className={CHECKBOX_CLASS}
              checked={field.state.value ?? false}
              onChange={(event) => {
                field.handleChange(event.target.checked);
                track('ui:connection_form_read_only', { engine, enabled: event.target.checked });
              }}
              onBlur={field.handleBlur}
              data-testid="connection-readonly-checkbox"
            />
            <label htmlFor="readOnly">Open read-only</label>
          </div>
        </FormRow>
      )}
    </form.Field>
  );
}

export function TargetSection(props: TargetSectionProps) {
  const { form, engine, authentication } = props;
  const section = targetSectionTitle(engine);
  const serverProps = {
    form,
    engine,
    onAzurePick: props.onAzurePick,
    entryDisabledReason: props.entryDisabledReason,
    defaultHint: props.defaultHint,
    onDismissDefaultHint: props.onDismissDefaultHint,
    serverCarriedFrom: props.serverCarriedFrom,
  };
  const signIn = <SignInRows form={form} engine={engine} authentication={authentication} />;
  return (
    // Keyed by kind: a Server section collapsed on SQL Server stays so on PostgreSQL, but a Cluster section starts open.
    <FormSection key={section.id} id={section.id} title={section.title}>
      {engine === 'azurestorage' ? (
        <>
          <AzureStorageConnectionFields
            form={form}
            serverRow={(mode, modeHint) => <ServerRow {...serverProps} label={mode === 'resource' ? 'Resource URL' : 'Storage account'} extra={modeHint} />}
            signInRows={signIn}
          />
          <ReadOnlyRow form={form} engine={engine} />
        </>
      ) : (
        <>
          {engine === 'oracle' && <OracleConnectByRow form={form} />}
          <ServerAndPortRows {...serverProps} />
          {engine === 'oracle' && <OracleTargetRows form={form} />}
          {engine === 'sqlite' && <ReadOnlyRow form={form} engine={engine} />}
          {signIn}
          <DatabaseRows form={form} engine={engine} loadedDatabases={props.loadedDatabases} onLoadDatabases={props.onLoadDatabases} loadingDatabases={props.loadingDatabases} />
        </>
      )}
    </FormSection>
  );
}
